import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Award, Flame, Gauge, ListChecks, Lock, Users } from "lucide-react";
import { FadeUp } from "@/components/landing/Reveal";

const STPS = [
  { id: "express", icon: Gauge, name: "Diagnóstico Exprés STPS", sub: "Evaluación rápida de riesgo", n: 12, tags: "NOM-001 · 002 · 017 · 019 · 030", badge: "Gratis" },
  { id: "nom002", icon: Flame, name: "NOM-002-STPS", sub: "Prevención y protección contra incendios", n: 10, tags: "Extintores · Evacuación · Detección · Brigadas · Simulacros", badge: "Plan Base" },
  { id: "nom019", icon: Users, name: "NOM-019-STPS", sub: "Comisión de seguridad e higiene", n: 8, tags: "Acta constitutiva · Paridad · Recorridos · Investigación", badge: "Plan Base" },
  { id: "nom002x", icon: Lock, name: "Inspección Exhaustiva NOM-002", sub: "Versión completa para plantas industriales", n: 35, tags: "Carga de fuego · Instalaciones · Brigadas · Trabajos en caliente", badge: "Plan Pro" },
];
const ISO_LIST = [
  { id: "iso9001", icon: Award, name: "ISO 9001:2015", sub: "Diagnóstico pre-auditoría de calidad", n: 10, tags: "Cláusulas 4 a 10 · Proveedores · Cliente · CAPA", badge: "Plan Base" },
  { id: "iso9001x", icon: Lock, name: "Inspección Exhaustiva ISO 9001", sub: "Capítulo por capítulo, rumbo a certificación", n: 45, tags: "Contexto · Liderazgo · Operación · Mejora", badge: "Plan Pro" },
];

const ModuleRow = ({ m, i }) => (
  <FadeUp delay={i * 0.06}>
    <motion.div whileHover="hover" className="group relative grid grid-cols-12 gap-4 items-start border-b border-white/10 py-7" data-testid={`landing-module-${m.id}`}>
      <motion.span variants={{ hover: { scaleX: 1 } }} initial={{ scaleX: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="absolute left-0 bottom-[-1px] h-px w-full bg-[#F59E0B] origin-left" />
      <m.icon className="col-span-2 sm:col-span-1 w-6 h-6 text-[#F59E0B] mt-1" strokeWidth={1.5} />
      <div className="col-span-10 sm:col-span-8 min-w-0">
        <motion.h4 variants={{ hover: { x: 8 } }} className="font-display text-2xl sm:text-3xl font-bold tracking-tight group-hover:text-[#F59E0B] transition-colors">
          {m.name} {m.badge && <span className={`align-middle ml-2 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] ${m.badge === "Plan Pro" ? "bg-[#F59E0B] text-[#0B0F17]" : m.badge === "Plan Base" ? "bg-sky-400 text-[#0B0F17]" : "bg-[#F5F1E8] text-[#0B0F17]"}`}>{m.badge}</span>}
        </motion.h4>
        <p className="mt-1 text-[#F5F1E8]/80">{m.sub}</p>
        <p className="mt-2 font-mono text-xs text-[#F5F1E8]/55">{m.tags}</p>
      </div>
      <div className="col-span-10 col-start-3 sm:col-span-3 sm:col-start-auto sm:text-right">
        <span className="font-display-x text-3xl font-black">{m.n}</span>
        <span className="block font-mono text-xs text-[#F5F1E8]/60">reactivos</span>
      </div>
    </motion.div>
  </FadeUp>
);

const PillarHead = ({ n, title, text }) => (
  <div>
    <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#F5F1E8]/60">Pilar {n}</div>
    <h3 className="font-display-x mt-3 text-3xl sm:text-4xl font-black uppercase leading-[0.95] tracking-tight">{title}</h3>
    <p className="mt-3 text-sm sm:text-base text-[#F5F1E8]/70 max-w-md">{text}</p>
  </div>
);

export const Catalog = () => (
  <section id="catalogo" className="relative py-24 lg:py-36 border-t border-white/10" data-testid="catalog-section">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-8 mb-14 lg:mb-20">
        <FadeUp className="lg:col-span-5 font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]">/ 02 — Catálogo de auditorías</FadeUp>
        <FadeUp delay={0.1} className="lg:col-span-7">
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.02]">
            Dos pilares, auditorías exprés y exhaustivas. <span className="text-[#F5F1E8]/45">Un solo portal de cumplimiento.</span>
          </h2>
        </FadeUp>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 flex flex-col gap-6">
        <div className="border border-white/10 bg-[#111722] p-6 sm:p-10" data-testid="pillar-stps">
          <div className="grid sm:grid-cols-5 gap-6 items-end">
            <div className="sm:col-span-3"><PillarHead n="I" title={<>Cumplimiento<br />legal STPS</>} text="Normas oficiales mexicanas de seguridad e higiene: desde un barrido exprés hasta auditorías especializadas por norma." /></div>
            <FadeUp delay={0.1} className="sm:col-span-2 clip-frame overflow-hidden aspect-[4/3] relative">
              <img src="/img/p3.jpg" alt="Trabajador con chaleco de alta visibilidad en planta" className="absolute inset-0 w-full h-full object-cover grayscale-[40%]" />
              <div className="absolute inset-0 bg-[#F59E0B]/10 mix-blend-multiply" />
            </FadeUp>
          </div>
          <div className="mt-8 border-t border-white/10">
            {STPS.map((m, i) => <ModuleRow key={m.id} m={m} i={i} />)}
          </div>
        </div>
          <FadeUp className="border border-[#F59E0B]/40 bg-[#F59E0B]/[0.06] p-6 sm:p-8 flex items-start gap-4" data-testid="catalog-fix-tracking">
            <ListChecks className="w-7 h-7 text-[#F59E0B] shrink-0" strokeWidth={1.5} />
            <div>
              <h4 className="font-display text-xl font-bold">Todos los “No”, en un solo plan de acción</h4>
              <p className="mt-2 text-sm text-[#F5F1E8]/75">Los hallazgos de cualquier norma se ordenan por prioridad (Crítica, Alta, Media) con su acción correctiva y plazo sugerido.</p>
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-5 flex">
          <div className="relative border border-white/10 bg-[#111722] p-6 sm:p-10 overflow-hidden flex-1 flex flex-col" data-testid="pillar-iso">
            <PillarHead n="II" title={<>Calidad y<br /><span className="text-[#F59E0B]">certificación ISO</span></>} text="Prepárate para la certificación: detecta brechas del sistema de gestión de calidad antes de la auditoría externa." />
            <FadeUp delay={0.1} className="mt-8 clip-notch overflow-hidden relative flex-1 min-h-[240px]">
              <img src="/img/p2.jpg" alt="Revisión de controles de calidad en proceso industrial" className="absolute inset-0 w-full h-full object-cover grayscale-[30%]" />
            </FadeUp>
            <div className="mt-4 border-t border-white/10">{ISO_LIST.map((m, i) => <ModuleRow key={m.id} m={m} i={i} />)}</div>
          </div>
        </div>
      </div>

      <FadeUp className="mt-12">
        <Link to="/registro" data-testid="catalog-start-button" className="group inline-flex items-center gap-3 bg-[#F59E0B] text-[#0B0F17] pl-7 pr-2 py-2 rounded-full font-semibold hover:bg-[#F5F1E8] transition-colors">
          Elegir mi primera auditoría
          <span className="grid place-items-center w-10 h-10 rounded-full bg-[#0B0F17] text-[#F59E0B] transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="w-4 h-4" /></span>
        </Link>
      </FadeUp>
    </div>
  </section>
);
